import React from "react";
import { ClinicalMapRegion, ClinicalMapType, ClinicalProtocolMarking } from "../../services/clinicalMapService";

type Props = {
  mapType: ClinicalMapType;
  regions: ClinicalMapRegion[];
  markings: ClinicalProtocolMarking[];
  selectedRegionId?: number | null;
  onSelectRegion: (region: ClinicalMapRegion) => void;
};

const outlineStroke = "#94a3b8";
const outlineFill = "#f8fafc";

const FaceOutline = () => (
  <g>
    <ellipse cx="50" cy="48" rx="28" ry="36" fill={outlineFill} stroke={outlineStroke} strokeWidth="1.5" />
    <ellipse cx="40" cy="42" rx="3.5" ry="2.2" fill="#cbd5e1" />
    <ellipse cx="60" cy="42" rx="3.5" ry="2.2" fill="#cbd5e1" />
    <path d="M46 58 Q50 62 54 58" fill="none" stroke="#cbd5e1" strokeWidth="1.2" />
    <path d="M42 88 Q50 96 58 88" fill="none" stroke={outlineStroke} strokeWidth="1.5" />
  </g>
);

const BodyOutline = () => (
  <g>
    <circle cx="50" cy="12" r="7" fill={outlineFill} stroke={outlineStroke} strokeWidth="1.5" />
    <path
      d="M38 22 H62 L70 48 H58 L56 88 H44 L42 48 H30 Z"
      fill={outlineFill}
      stroke={outlineStroke}
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M30 30 L18 52" stroke={outlineStroke} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M70 30 L82 52" stroke={outlineStroke} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M44 88 L40 98" stroke={outlineStroke} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M56 88 L60 98" stroke={outlineStroke} strokeWidth="1.5" strokeLinecap="round" />
  </g>
);

const ClinicalMapSchematic: React.FC<Props> = ({
  mapType,
  regions,
  markings,
  selectedRegionId,
  onSelectRegion,
}) => {
  const counts = React.useMemo(() => {
    const map = new Map<number, number>();
    markings.forEach((m) => {
      map.set(m.clinical_map_region_id, (map.get(m.clinical_map_region_id) ?? 0) + 1);
    });
    return map;
  }, [markings]);

  const withHotspot = regions.filter((r) => r.has_hotspot && r.is_active);
  const withoutHotspot = regions.filter((r) => !r.has_hotspot && r.is_active);

  return (
    <div>
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label={mapType === "face" ? "Mapa facial" : "Mapa corporal"}
        style={{ width: "100%", maxWidth: 280, height: "auto", display: "block", margin: "0 auto" }}
      >
        {mapType === "face" ? <FaceOutline /> : <BodyOutline />}
        {withHotspot.map((region) => {
          const count = counts.get(region.id) ?? 0;
          const selected = selectedRegionId === region.id;
          const cx = region.hotspot_x_pct ?? 50;
          const cy = region.hotspot_y_pct ?? 50;
          return (
            <g
              key={region.id}
              style={{ cursor: "pointer" }}
              onClick={() => onSelectRegion(region)}
            >
              <title>{region.name}</title>
              <circle
                cx={cx}
                cy={cy}
                r={selected ? 5.5 : 4.5}
                fill={count > 0 ? "#0ab39c" : selected ? "#405189" : "#ffffff"}
                stroke={selected ? "#405189" : count > 0 ? "#0ab39c" : "#64748b"}
                strokeWidth={selected ? 1.8 : 1.2}
              />
              {count > 0 ? (
                <text
                  x={cx}
                  y={cy + 1.2}
                  textAnchor="middle"
                  fontSize="4.5"
                  fill="#fff"
                  fontWeight="700"
                  style={{ pointerEvents: "none" }}
                >
                  {count > 9 ? "9+" : count}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>

      {withoutHotspot.length > 0 ? (
        <div className="d-flex flex-wrap gap-1 justify-content-center mt-2">
          {withoutHotspot.map((region) => {
            const count = counts.get(region.id) ?? 0;
            const selected = selectedRegionId === region.id;
            return (
              <button
                key={region.id}
                type="button"
                className={`btn btn-sm ${selected ? "btn-primary" : count > 0 ? "btn-soft-success" : "btn-soft-secondary"}`}
                onClick={() => onSelectRegion(region)}
              >
                {region.name}
                {count > 0 ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ClinicalMapSchematic;
